import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, IndianRupee, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

const MODES = [
  { key: "upi", label: "UPI" },
  { key: "gpay", label: "GPay" },
  { key: "phonepe", label: "PhonePe" },
  { key: "bank", label: "Bank Transfer" },
];

export default function Withdraw() {
  const nav = useNavigate();
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("upi");
  const [acc, setAcc] = useState("");
  const [bal, setBal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const b = await api.get("/customer/wallet");
    setBal(b.data.saving_balance);
    const r = await api.get("/customer/withdrawals");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    if (amt > bal) { toast.error("Insufficient saving balance"); return; }
    setLoading(true);
    try {
      await api.post("/customer/wallet/withdraw", { amount: amt, mode, account_info: acc });
      toast.success("Withdrawal requested. Awaiting admin approval.");
      setAmount(""); setAcc("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="withdraw-page">
      <div className="bg-tenant-gradient text-white px-5 pt-6 pb-6 flex items-center gap-3">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">Withdraw</div>
      </div>

      <div className="px-5 pt-6">
        <div className="bg-white border border-slate-100 rounded-2xl p-4 card-shadow">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400">Available Balance</div>
          <div className="text-2xl font-extrabold mt-1" data-testid="withdraw-available-balance">{fmtINR(bal)}</div>
        </div>

        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mt-5 block">Withdraw Amount</label>
        <div className="mt-2 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-3.5 bg-white">
          <IndianRupee className="w-5 h-5 text-slate-400" />
          <input data-testid="withdraw-amount" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" inputMode="decimal" className="flex-1 outline-none text-xl font-extrabold" />
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {MODES.map((m) => (
            <button key={m.key} data-testid={`withdraw-mode-${m.key}`} onClick={() => setMode(m.key)} className={`py-2.5 rounded-xl text-xs font-bold tap-scale ${mode === m.key ? "bg-tenant-primary text-white" : "bg-white border border-slate-200"}`}>
              {m.label}
            </button>
          ))}
        </div>

        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mt-5 block">Account Info (UPI ID / Bank A/C)</label>
        <input data-testid="withdraw-account" value={acc} onChange={(e) => setAcc(e.target.value)} placeholder="example@upi" className="mt-2 w-full border-2 border-slate-200 rounded-2xl px-4 py-3 outline-none focus:border-tenant-primary" />

        <button data-testid="confirm-withdraw" onClick={submit} disabled={loading} className="w-full mt-6 bg-tenant-primary text-white py-3.5 rounded-2xl font-bold tap-scale disabled:opacity-60">
          {loading ? "Submitting..." : "Submit Request"}
        </button>

        <div className="mt-8">
          <h3 className="font-extrabold tracking-tight mb-3">My Requests</h3>
          <div className="space-y-2">
            {items.map((i) => (
              <div key={i.id} className="bg-white border border-slate-100 rounded-2xl p-3 flex items-center justify-between card-shadow">
                <div>
                  <div className="font-bold">{fmtINR(i.amount)} via {i.mode.toUpperCase()}</div>
                  <div className="text-[11px] text-slate-400">{fmtDate(i.created_at)}</div>
                </div>
                <StatusPill status={i.status} />
              </div>
            ))}
            {items.length === 0 && <div className="text-center text-slate-400 text-sm py-6">No requests yet.</div>}
          </div>
        </div>
        <div className="h-10" />
      </div>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const map = {
    pending: { c: "bg-amber-50 text-amber-700", I: Clock },
    approved: { c: "bg-emerald-50 text-emerald-700", I: CheckCircle2 },
    rejected: { c: "bg-rose-50 text-rose-700", I: XCircle },
  };
  const { c, I } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${c}`}>
      <I className="w-3 h-3" /> {status}
    </span>
  );
};
